import { FC, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useStore } from '@hooks/useStore';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { Button } from '@components/Core/Button';
import { Input } from '@inputs/Input';
import { ChangeProfileNamePopupTransFields, IChangeProfileNamePopupForm } from './types';
import { changeProfileNamePopupTranslations } from './translations';

export const ChangeProfileNamePopup: FC = () => {
  const {
    popupStore: { closePopup },
    profileStore: { user, changeProfile },
    alertStore: { showAlert },
  } = useStore();

  const useFormMethods = useForm<IChangeProfileNamePopupForm>({
    defaultValues: { name: user?.name || '' },
  });

  const {
    handleSubmit,
    formState: { isDirty },
  } = useFormMethods;

  const { t } = useMyTranslations<ChangeProfileNamePopupTransFields>([
    changeProfileNamePopupTranslations.name,
  ]);

  const { mutate, isPending } = useMutation({
    mutationFn: changeProfile,
    mutationKey: ['changeProfile'],
    onSuccess: () => {
      closePopup();
      showAlert('Your name was changed succesfully', 'success');
    },
  });

  const onSubmit = useCallback((formData: IChangeProfileNamePopupForm) => {
    mutate(formData);
  }, []);

  return (
    <div className="flex flex-col justify-stretch items-center gap-y-[16px]">
      <p className="default-text mb-[8px] text-black text-center">{t('Edit Name')}</p>
      <FormProvider {...useFormMethods}>
        <Input name="name" styleProp="mb-[16px] h-[40px]" required />
      </FormProvider>
      <Button
        text={t('Save Changes')}
        onClick={handleSubmit(onSubmit)}
        disabled={!isDirty || isPending}
      />
      <Button text={t('Cancel')} onClick={closePopup} styleType="secondary" />
    </div>
  );
};
